import { readStored, writeStored } from './persisted.js'

export interface RememberedList {
  token: string
  role: 'edit' | 'view' | 'admin'
  title: string
  emoji: string | null
  total: number
  checked: number
  lastVisited: string
  missing?: boolean
}

const KEY = 'streepaf.myLists'
const MAX = 40

/**
 * Without accounts this is the only memory a person has of their lists, so it
 * is written on every visit and kept even when a list turns out to be gone.
 */
class MyLists {
  entries = $state<RememberedList[]>([])

  init(): void {
    this.entries = readStored<RememberedList[]>(KEY, []).filter((entry) => entry?.token)
  }

  private save(): void {
    writeStored(KEY, this.entries)
  }

  remember(entry: Omit<RememberedList, 'lastVisited'>): void {
    const rest = this.entries.filter((existing) => existing.token !== entry.token)
    // A stronger role for the same list replaces the weaker one.
    const superseded = rest.filter(
      (existing) => !(existing.title === entry.title && rank(existing.role) < rank(entry.role)),
    )
    this.entries = [{ ...entry, lastVisited: new Date().toISOString() }, ...superseded].slice(
      0,
      MAX,
    )
    this.save()
  }

  update(token: string, patch: Partial<RememberedList>): void {
    const index = this.entries.findIndex((entry) => entry.token === token)
    if (index === -1) return
    const current = this.entries[index]
    if (!current) return
    const next = [...this.entries]
    next[index] = { ...current, ...patch }
    this.entries = next
    this.save()
  }

  markMissing(token: string): void {
    this.update(token, { missing: true })
  }

  forget(token: string): void {
    this.entries = this.entries.filter((entry) => entry.token !== token)
    this.save()
  }
}

function rank(role: RememberedList['role']): number {
  return role === 'admin' ? 3 : role === 'edit' ? 2 : 1
}

export const myLists = new MyLists()
